import { Box, useTheme } from "@mui/material"
import {
    Delete as DeleteIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

import {
    ResponsiveSignaturePad
} from "@initia-digital/signature-pad";
import {
    GeneralChip,
    IconButton,
    Textfield
} from "@initia-digital/component-library";
import { ISiteContact } from "@initia-digital/types/work-and-safety";
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";


export interface ISiteContactProps {
    siteContact: ISiteContact
    handleSiteContactUpdate: (siteContactUpdate: ISiteContact) => void
    handleSiteContactDelete: (siteContact: ISiteContact) => void
}

export const SiteContact = ({
    siteContact,
    handleSiteContactUpdate,
    handleSiteContactDelete
}: ISiteContactProps) => {
    // ---------------------
    const {
        contactName,
        contactEmail,
        contactNumber,
        contactOrg,
        contactSignatureClientData
    } = siteContact;

    const emailEnabled = baseDefinedAndNotNullNarrowing(contactEmail);
    const numberEnabled = baseDefinedAndNotNullNarrowing(contactNumber);
    const orgEnabled = baseDefinedAndNotNullNarrowing(contactOrg);

    const theme = useTheme();

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
            position="relative"
            overflow="hidden"
            p="10px"
            border="1px solid lightgrey"
        >
            <Box>
            <Box
                display="flex" flexDirection="row"
                justifyContent="flex-end"
            >
                <IconButton
                    tooltipTitle="Close"
                    tooltipPlacement="top"
                    onClick={() => {
                        // ---------------------
                        handleSiteContactDelete(siteContact);
                    }}
                    aria-label="delete-site-contact"
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
            <Box mb="4px">
                <Textfield
                    fontSize="0.8rem"
                    value={contactName ?? ''}
                    name="contactName"
                    label="Name"
                    placeholder="Name"
                    handleChange={(event) => {
                        // ---------------------
                        handleSiteContactUpdate({
                            ...siteContact,
                            contactName: event.target.value
                        });
                    }}
                />
            </Box>
            {
                emailEnabled &&
                <Box mb="4px" display="flex" alignItems="center" gap="4px">
                    <Box flex={1}>
                        <Textfield
                            fontSize="0.8rem"
                            value={contactEmail ?? ''}
                            name="contactEmail"
                            label="Email"
                            placeholder="Email"
                            handleChange={(event) => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactEmail: event.target.value
                                });
                            }}
                        />
                    </Box>
                    <IconButton
                        tooltipTitle="Remove email field"
                        tooltipPlacement="top"
                        onClick={() => {
                            // ---------------------
                            handleSiteContactUpdate({
                                ...siteContact,
                                contactEmail: null
                            });
                        }}
                        aria-label="remove-email-field"
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
            }
            {
                numberEnabled &&
                <Box mb="4px" display="flex" alignItems="center" gap="4px">
                    <Box flex={1}>
                        <Textfield
                            fontSize="0.8rem"
                            value={contactNumber ?? ''}
                            name="contactNumber"
                            label="Number"
                            placeholder="Number"
                            handleChange={(event) => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactNumber: event.target.value
                                });
                            }}
                        />
                    </Box>
                    <IconButton
                        tooltipTitle="Remove phone field"
                        tooltipPlacement="top"
                        onClick={() => {
                            // ---------------------
                            handleSiteContactUpdate({
                                ...siteContact,
                                contactNumber: null
                            });
                        }}
                        aria-label="remove-phone-field"
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
            }
            {
                orgEnabled &&
                <Box mb="4px" display="flex" alignItems="center" gap="4px">
                    <Box flex={1}>
                        <Textfield
                            fontSize="0.8rem"
                            value={contactOrg ?? ''}
                            name="contactOrg"
                            label="Organisation"
                            placeholder="Organisation"
                            handleChange={(event) => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactOrg: event.target.value
                                });
                            }}
                        />
                    </Box>
                    <IconButton
                        tooltipTitle="Remove organisation field"
                        tooltipPlacement="top"
                        onClick={() => {
                            // ---------------------
                            handleSiteContactUpdate({
                                ...siteContact,
                                contactOrg: null
                            });
                        }}
                        aria-label="remove-org-field"
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
            }
            {
                (!emailEnabled || !numberEnabled || !orgEnabled) &&
                <Box
                    display="flex" flexDirection="row" flexWrap="wrap"
                    columnGap="4px" mb="8px"
                >
                    {
                        !emailEnabled &&
                        <GeneralChip
                            size="vsmall"
                            label="Add Email"
                            variant="outlined"
                            color={theme.palette.secondary.main}
                            borderColor={theme.palette.secondary.main}
                            clickable
                            handleClick={() => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactEmail: ''
                                });
                            }}
                        />
                    }
                    {
                        !numberEnabled &&
                        <GeneralChip
                            size="vsmall"
                            label="Add Phone"
                            variant="outlined"
                            color={theme.palette.secondary.main}
                            borderColor={theme.palette.secondary.main}
                            clickable
                            handleClick={() => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactNumber: ''
                                });
                            }}
                        />
                    }
                    {
                        !orgEnabled &&
                        <GeneralChip
                            size="vsmall"
                            label="Add Organisation"
                            variant="outlined"
                            color={theme.palette.secondary.main}
                            borderColor={theme.palette.secondary.main}
                            clickable
                            handleClick={() => {
                                // ---------------------
                                handleSiteContactUpdate({
                                    ...siteContact,
                                    contactOrg: ''
                                });
                            }}
                        />
                    }
                </Box>
            }
            </Box>
            <Box
                display="flex"
                flexDirection="column"
                position="relative"
                width="100%"
                overflow="hidden"
            >
                <ResponsiveSignaturePad
                    handleSignatureSave={(points: SignaturePad.Point[][]) => {
                        // ---------------------
                        handleSiteContactUpdate({
                            ...siteContact,
                            contactSignatureClientData: points
                        });
                    }}
                    onClear={() => {
                        // ---------------------
                        handleSiteContactUpdate({
                            ...siteContact,
                            contactSignatureClientData: []
                        });
                    }}
                    showGreyZone={true}
                    greyZonePaddingY={30}
                    signatureData={contactSignatureClientData ?? []}
                    aspectRatio={0.65}
                />
            </Box>
        </Box>
    )
}